import type {
  BalancePolicy,
  RemittanceAllocationConfig,
  RemittanceAllocationRule,
  RemittanceAllocationTarget,
} from '@capture/shared';
import { slugify } from '@/lib/slugify';
import { useFormConfigStore } from '../state/store';
import { useRemittanceIssues } from './useRemittanceIssues';
import { usePublishedTargetIds } from './usePublishedTargetIds';
import './AllocationSection.css';

export interface AllocationSectionProps {
  /** Threaded from JobTypeDetailPage so the id-lock can check the last
   *  published version's targets. Omitted in contexts (or tests) that have
   *  no job type in scope — the lock simply falls back to draft-only checks. */
  jobTypeId?: string;
}

const EMPTY_TARGETS: RemittanceAllocationTarget[] = [];
const RULE_TYPES = ['none', 'ratio', 'priority'] as const;
type RuleTypeOption = (typeof RULE_TYPES)[number];

/** SP6 Task 5 target-id contract: shared `slugify` with the underscore
 *  convention, falling back to a positional id for label-less rows. */
function slugFromLabel(label: string, rowNumber: number): string {
  return slugify(label, '_') || `target_${rowNumber}`;
}

/** A reason a target id is locked. `kind` drives behavior (F-012 — only `rule`
 *  and `policy` block removal; `published` locks identity but not deletion);
 *  `label` is the display copy. Empty when unreferenced. */
type LockReason = { kind: 'rule' | 'policy' | 'published'; label: string };

function lockReasons(
  targetId: string,
  allocation: RemittanceAllocationConfig | undefined,
  balancePolicy: BalancePolicy | undefined,
  publishedIds: Set<string>,
): LockReason[] {
  if (!targetId) return [];
  const reasons: LockReason[] = [];

  const rule = allocation?.rule;
  const referencedByRule =
    (rule?.type === 'ratio' && Object.prototype.hasOwnProperty.call(rule.ratios, targetId)) ||
    (rule?.type === 'priority' && rule.order.includes(targetId));
  if (referencedByRule) reasons.push({ kind: 'rule', label: 'the allocation rule' });

  const referencedByPolicy = ([balancePolicy?.shortPay, balancePolicy?.overPay] as const).some((side) => {
    const dr = side?.deltaResolution;
    if (!dr) return false;
    if (dr.defaultTargetId === targetId) return true;
    return (dr.rules ?? []).some((r) => r.targetId === targetId);
  });
  if (referencedByPolicy) reasons.push({ kind: 'policy', label: 'the balance policy' });

  if (publishedIds.has(targetId)) reasons.push({ kind: 'published', label: 'the published version' });

  return reasons;
}

/**
 * SP6 Task 5 — Allocation section of the Remittance job-config tab. Renders
 * the targets table (add/remove/edit, id-locking) and the prescribed-rule
 * editor (none/ratio/priority). Every write recomputes and sends the FULL
 * `RemittanceAllocationConfig` via `updateRemittance({ allocation })` — the
 * store's merge is shallow, so a partial patch would silently drop the other
 * half of the config.
 */
export function AllocationSection({ jobTypeId }: AllocationSectionProps) {
  const remittance = useFormConfigStore((s) => s.contentV3?.remittance);
  const updateRemittance = useFormConfigStore((s) => s.updateRemittance);
  const issues = useRemittanceIssues();
  const publishedIds = usePublishedTargetIds(jobTypeId);

  const allocation = remittance?.allocation;
  const balancePolicy = remittance?.balancePolicy;
  const targets = allocation?.targets ?? EMPTY_TARGETS;
  const rule = allocation?.rule;

  const sectionIssues = [
    ...issues.allocation,
    ...issues.policy.filter((i) => i.code === 'remittance_policy_target_unknown'),
  ];

  function writeTargets(nextTargets: RemittanceAllocationTarget[]) {
    updateRemittance({ allocation: { targets: nextTargets, ...(rule ? { rule } : {}) } });
  }

  function writeRule(nextRule: RemittanceAllocationRule | undefined) {
    updateRemittance({ allocation: nextRule ? { targets, rule: nextRule } : { targets } });
  }

  function handleAddTarget() {
    writeTargets([...targets, { id: '', label: '' }]);
  }

  function handleRemoveTarget(index: number) {
    writeTargets(targets.filter((_, i) => i !== index));
  }

  function handleLabelChange(index: number, label: string) {
    writeTargets(targets.map((t, i) => (i === index ? { ...t, label } : t)));
  }

  function handleLabelBlur(index: number) {
    const target = targets[index];
    if (!target || target.id.trim() !== '') return; // id already set (seeded or already generated) — never clobber
    writeTargets(
      targets.map((t, i) => (i === index ? { ...t, id: slugFromLabel(t.label, i + 1) } : t)),
    );
  }

  function handleIdChange(index: number, id: string) {
    writeTargets(targets.map((t, i) => (i === index ? { ...t, id } : t)));
  }

  function handleCodeChange(index: number, code: string) {
    writeTargets(targets.map((t, i) => (i === index ? { ...t, code: code === '' ? undefined : code } : t)));
  }

  function handleRuleTypeChange(type: RuleTypeOption) {
    if (type === (rule?.type ?? 'none')) return;
    if (type === 'none') writeRule(undefined);
    else if (type === 'ratio') writeRule({ type: 'ratio', ratios: {} });
    else writeRule({ type: 'priority', order: [] });
  }

  function handleRatioChange(targetId: string, raw: string) {
    if (rule?.type !== 'ratio') return;
    const nextRatios = { ...rule.ratios };
    if (raw === '') {
      delete nextRatios[targetId];
    } else {
      const n = Number(raw);
      if (Number.isNaN(n)) return;
      nextRatios[targetId] = n;
    }
    writeRule({ type: 'ratio', ratios: nextRatios });
  }

  function movePriority(index: number, direction: -1 | 1) {
    if (rule?.type !== 'priority') return;
    const target = index + direction;
    if (target < 0 || target >= rule.order.length) return;
    const order = [...rule.order];
    [order[index], order[target]] = [order[target]!, order[index]!];
    writeRule({ type: 'priority', order });
  }

  function addToPriorityOrder(targetId: string) {
    if (rule?.type !== 'priority' || !targetId || rule.order.includes(targetId)) return;
    writeRule({ type: 'priority', order: [...rule.order, targetId] });
  }

  function removeFromPriorityOrder(targetId: string) {
    if (rule?.type !== 'priority') return;
    writeRule({ type: 'priority', order: rule.order.filter((id) => id !== targetId) });
  }

  const ratioSum =
    rule?.type === 'ratio' ? Object.values(rule.ratios).reduce((sum, n) => sum + (Number.isFinite(n) ? n : 0), 0) : 0;
  const unorderedTargets = rule?.type === 'priority' ? targets.filter((t) => !rule.order.includes(t.id)) : [];

  return (
    <section className="remittance-allocation">
      <h3 className="remittance-allocation__heading">Allocation</h3>

      {sectionIssues.length > 0 && (
        <ul className="remittance-allocation__issues" role="alert">
          {sectionIssues.map((issue, i) => (
            <li key={`${issue.code}-${i}`}>{issue.message}</li>
          ))}
        </ul>
      )}

      <table className="remittance-allocation__table">
        <thead>
          <tr>
            <th>Id</th>
            <th>Label</th>
            <th>Code</th>
            <th aria-label="Row actions" />
          </tr>
        </thead>
        <tbody>
          {targets.map((target, index) => {
            const reasons = lockReasons(target.id, allocation, balancePolicy, publishedIds);
            const locked = reasons.length > 0;
            // F-012 — Remove blocks only on LIVE references (rule/policy):
            // deleting would strand dangling refs. Mere presence in the
            // published version locks the id (identity is immutable) but must
            // NOT prevent removing the target from the next version. Keyed on
            // reason.kind, not the display label, so wording edits can't flip
            // which targets are deletable.
            const removeReasons = reasons.filter((r) => r.kind !== 'published');
            const removeLocked = removeReasons.length > 0;
            return (
              // eslint-disable-next-line react/no-array-index-key -- rows have no stable key until an id is generated
              <tr key={index}>
                <td>
                  <input
                    aria-label={`Target ${index + 1} id`}
                    value={target.id}
                    disabled={locked}
                    title={locked ? `referenced by ${reasons.map((r) => r.label).join(', ')}` : undefined}
                    onChange={(e) => handleIdChange(index, e.target.value)}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Target ${index + 1} label`}
                    value={target.label}
                    onChange={(e) => handleLabelChange(index, e.target.value)}
                    onBlur={() => handleLabelBlur(index)}
                  />
                </td>
                <td>
                  <input
                    aria-label={`Target ${index + 1} code`}
                    value={target.code ?? ''}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                  />
                </td>
                <td>
                  {/* F-012 — a rule/policy-referenced target refuses deletion
                      (design): a delete would silently strand dangling
                      references. */}
                  <button
                    type="button"
                    disabled={removeLocked}
                    title={removeLocked ? `referenced by ${removeReasons.map((r) => r.label).join(', ')}` : undefined}
                    onClick={() => handleRemoveTarget(index)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <button type="button" className="remittance-allocation__add-target" onClick={handleAddTarget}>
        Add target
      </button>

      <div className="remittance-allocation__rule">
        <h4 className="remittance-allocation__rule-heading">Prescribed rule</h4>
        <div className="remittance-allocation__rule-type" role="radiogroup" aria-label="Rule type">
          {RULE_TYPES.map((type) => (
            <label key={type}>
              <input
                type="radio"
                name="allocation-rule-type"
                value={type}
                checked={(rule?.type ?? 'none') === type}
                onChange={() => handleRuleTypeChange(type)}
              />
              {type}
            </label>
          ))}
        </div>

        {rule?.type === 'ratio' && (
          <div className="remittance-allocation__ratios">
            {targets.map((target) => (
              <label key={target.id || target.label} className="remittance-allocation__ratio-field">
                {target.label || target.id || 'Untitled target'}
                <input
                  type="number"
                  aria-label={`Ratio weight for ${target.label || target.id}`}
                  value={rule.ratios[target.id] ?? ''}
                  onChange={(e) => handleRatioChange(target.id, e.target.value)}
                />
              </label>
            ))}
            <p className="remittance-allocation__ratio-sum">Sum: {ratioSum}</p>
          </div>
        )}

        {rule?.type === 'priority' && (
          <div className="remittance-allocation__priority">
            <ol>
              {rule.order.map((targetId, index) => {
                const target = targets.find((t) => t.id === targetId);
                const name = target?.label || targetId;
                return (
                  <li key={targetId}>
                    <span>{name}</span>
                    <button
                      type="button"
                      onClick={() => movePriority(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move ${name} up`}
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      onClick={() => movePriority(index, 1)}
                      disabled={rule.order.length === 0 || index === rule.order.length - 1}
                      aria-label={`Move ${name} down`}
                    >
                      Down
                    </button>
                    <button type="button" onClick={() => removeFromPriorityOrder(targetId)} aria-label={`Remove ${name} from order`}>
                      Remove
                    </button>
                  </li>
                );
              })}
            </ol>
            {unorderedTargets.length > 0 && (
              <label className="remittance-allocation__priority-add">
                Add to order
                <select
                  aria-label="Add target to priority order"
                  value=""
                  onChange={(e) => addToPriorityOrder(e.target.value)}
                >
                  <option value="" disabled>
                    Select a target
                  </option>
                  {unorderedTargets.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label || t.id}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
